/**
 * Reviews Data Model — Community Feedback System
 * Restaurant reviews with ratings, photos, and engagement
 */

// Sample reviews data (would come from API in production)
export const SAMPLE_REVIEWS = {
  1: [
    {
      id: 'r1',
      userId: 'u1',
      userName: 'Michael Chen',
      userAvatar: null,
      userLevel: 'Gold Foodie',
      rating: 5,
      categoryRatings: {
        food: 5,
        service: 4,
        atmosphere: 4,
        value: 4
      },
      title: 'Worth the wait!',
      content: 'The crab omelette is absolutely legendary. We arrived at 10:30am and there was already a queue, but it moved quickly. The omelette itself is huge and packed with real crab meat. Definitely worth the hype and the price.',
      dishes: ['Crab Omelette'],
      images: [],
      visitDate: '2025-01-15',
      createdAt: '2025-01-16T08:30:00Z',
      helpful: 24,
      reply: null,
      badges: ['verified', 'helpful-reviewer'],
      language: 'en'
    },
    {
      id: 'r2',
      userId: 'u2',
      userName: 'สมชาย รักอาหาร',
      userAvatar: null,
      userLevel: 'Silver Foodie',
      rating: 4,
      categoryRatings: {
        food: 5,
        service: 3,
        atmosphere: 3,
        value: 4
      },
      title: 'อร่อยมากแต่รอนาน',
      content: 'ไข่เจียวปูอร่อยจริงๆ ปูเยอะมาก แต่ต้องรอคิวนานพอสมควร พนักงานเร่งมากทำให้รู้สึกเร่งรีบ แต่โดยรวมแล้วประทับใจ',
      dishes: ['Crab Omelette', 'Stir-fried Cabbage'],
      images: [],
      visitDate: '2025-01-10',
      createdAt: '2025-01-11T15:20:00Z',
      helpful: 18,
      reply: null,
      badges: ['verified'],
      language: 'th'
    }
  ]
};

// Review statistics for restaurants
export const REVIEW_STATS = {
  1: {
    averageRating: 4.6,
    totalReviews: 1247,
    ratingDistribution: {
      5: 847,
      4: 298,
      3: 72,
      2: 21,
      1: 9
    },
    categoryAverages: {
      food: 4.8,
      service: 4.3,
      atmosphere: 4.2,
      value: 3.9
    },
    lastUpdated: '2025-01-16T00:00:00Z'
  }
};

// User levels for gamification
export const USER_LEVELS = {
  'Newcomer': { minReviews: 0, icon: '🌱', color: '#9CA3AF' },
  'Bronze Foodie': { minReviews: 5, icon: '🥉', color: '#CD7F32' },
  'Silver Foodie': { minReviews: 15, icon: '🥈', color: '#C0C0C0' },
  'Gold Foodie': { minReviews: 30, icon: '🥇', color: '#FFD700' },
  'Platinum Foodie': { minReviews: 50, icon: '💎', color: '#E5E4E2' },
  'Diamond Foodie': { minReviews: 100, icon: '💠', color: '#B9F2FF' }
};

// Review badges
export const REVIEW_BADGES = {
  'verified': { label: 'Verified Visit', icon: '✓', color: '#10B981' },
  'helpful-reviewer': { label: 'Helpful Reviewer', icon: '👍', color: '#3B82F6' },
  'photo-contributor': { label: 'Photo Contributor', icon: '📷', color: '#8B5CF6' },
  'top-reviewer': { label: 'Top Reviewer', icon: '⭐', color: '#F59E0B' }
};

/**
 * Generate unique review ID
 */
export function generateReviewId() {
  return `r_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Calculate user level based on review count
 */
export function calculateUserLevel(reviewCount) {
  let currentLevel = 'Newcomer';
  for (const [level, data] of Object.entries(USER_LEVELS)) {
    if (reviewCount >= data.minReviews) {
      currentLevel = level;
    }
  }
  return currentLevel;
}

/**
 * Format relative time for reviews
 */
export function formatRelativeTime(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffWeeks < 4) return `${diffWeeks}w ago`;
  if (diffMonths < 12) return `${diffMonths}mo ago`;
  return `${diffYears}y ago`;
}

/**
 * Calculate review stats from reviews array
 */
export function calculateReviewStats(reviews) {
  if (!reviews || reviews.length === 0) {
    return {
      averageRating: 0,
      totalReviews: 0,
      ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
      categoryAverages: { food: 0, service: 0, atmosphere: 0, value: 0 }
    };
  }

  const totalReviews = reviews.length;
  const sumRating = reviews.reduce((sum, r) => sum + r.rating, 0);
  const averageRating = sumRating / totalReviews;

  const ratingDistribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  const categorySums = { food: 0, service: 0, atmosphere: 0, value: 0 };
  const categoryCounts = { food: 0, service: 0, atmosphere: 0, value: 0 };

  reviews.forEach(r => {
    ratingDistribution[r.rating] = (ratingDistribution[r.rating] || 0) + 1;
    if (r.categoryRatings) {
      Object.keys(categorySums).forEach(key => {
        if (r.categoryRatings[key]) {
          categorySums[key] += r.categoryRatings[key];
          categoryCounts[key]++;
        }
      });
    }
  });

  const categoryAverages = {};
  Object.keys(categorySums).forEach(key => {
    categoryAverages[key] = categoryCounts[key] > 0
      ? categorySums[key] / categoryCounts[key]
      : 0;
  });

  return {
    averageRating,
    totalReviews,
    ratingDistribution,
    categoryAverages
  };
}

export default {
  SAMPLE_REVIEWS,
  REVIEW_STATS,
  USER_LEVELS,
  REVIEW_BADGES,
  generateReviewId,
  calculateUserLevel,
  formatRelativeTime,
  calculateReviewStats
};
